import { Form, Input, Select } from 'antd'
import { type FC, type MutableRefObject, useCallback, useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { FaTimes } from 'react-icons/fa'

import {
  Button,
  NormalizedSingleFieldDateTimePicker
} from '@/components/elements'
import { type SingleOnChange } from '@/components/types'
import { LABELS, STATUSES } from '@/constants/common/message.constants'
import { AreaSelect } from '@/features/areas/components'
import { useConfig } from '@/features/config/hooks'
import { SiteSelectWithLocation } from '@/features/sites/components'
import { type SiteSelectListExEntity, type WorkType } from '@/libs/api'
import { WORK_TYPE_OPTIONS } from '@/libs/api/types'

import { validateWaypointEntryTimes } from '../../models'

type WaypointFormValue = {
  siteId: number | null
  note: string | null
  areaId?: number | null
  workType?: WorkType | null
  entryTime?: Date | string | null
}

type Props = {
  /** useFieldArray の行 index */
  index: number
  tenantKey: string
  /** 荷主ID（SiteSelectWithLocation の「おすすめ」絞り込み用） */
  customerId?: number | null
  /** フィールドパス接頭辞（末尾ドット込み、例 `shipments.0.`）。既定 `''` */
  namePrefix?: string
  /**
   * 到着予定時刻（`waypoints.{i}.entryTime`、#5548）の入力欄を出す。配車板の矢羽根に
   * 載る依頼（作成・編集モーダル）だけ true。未配車の依頼フォームは時刻を持たない
   */
  showEntryTime?: boolean
  /**
   * 到着予定時刻のカレンダーで選べる日の範囲。矢羽根の時間帯（発地行・着地行の時間欄）を
   * 渡す。保存時の検証と同じ出どころにして、見えている制約と弾かれる制約を揃える
   */
  entryTimeRange?: {
    startAt: Date | string | null | undefined
    endAt: Date | string | null | undefined
  }
  /**
   * 到着予定時刻の検証の連番（`validateEntryTimes` 参照）。検証は全行をまとめて見直すので、
   * 連番は行をまたいで 1 本でなければならない。行ごとに持つと、行 0 の古い非同期検証が
   * 行 1 の最新の検証より後に終わったとき自行の連番では最新に見えて古いエラーを付け直す。
   * そのため `ShipmentOrderWaypointsSection` が 1 つ持って全行に渡す
   */
  entryTimeValidationSeqRef: MutableRefObject<number>
  onRemove: (index: number) => void
}

/**
 * 経由地 1 行分の入力 UI（エリア絞り込み → 地点選択 → 作業内容・備考 [→ 到着時刻] + 行削除）。
 *
 * 発地・着地と同じ AreaSelect + SiteSelectWithLocation を使う。絞り込みに使ったエリアは
 * フォーム値（`waypoints.{i}.areaId`）として保存し、開き直した時に復元する（#4733）。
 * 発地・着地・他経由地と同一地点は重複選択を抑止する。
 * 作業内容（`waypoints.{i}.workType`、#4813）は経由地だけが持つ項目（積込 / 荷降し / その他）で、既定値は「その他」。
 * 備考は発着地備考と同じ扱いで、地点選択時に空欄なら地点マスタの備考を初期補完する。
 * 到着時刻（#5548）は配車板のモーダルだけが出す。未入力可で、配送ルートの並びに使う。
 */
export const ShipmentOrderWaypointRow: FC<Props> = ({
  index,
  tenantKey,
  customerId,
  namePrefix = '',
  showEntryTime = false,
  entryTimeRange,
  entryTimeValidationSeqRef,
  onRemove
}) => {
  const {
    control,
    resetField,
    getValues,
    setValue,
    setError,
    clearErrors,
    trigger
  } = useFormContext()
  const { areas } = useConfig()
  const fieldName = `${namePrefix}waypoints.${index}.siteId`
  const noteFieldName = `${namePrefix}waypoints.${index}.note`
  const areaFieldName = `${namePrefix}waypoints.${index}.areaId`
  const workTypeFieldName = `${namePrefix}waypoints.${index}.workType`
  const entryTimeFieldName = `${namePrefix}waypoints.${index}.entryTime`

  // #4733: エリアはフォーム値。以前は行ローカル state だったため保存されず、
  // 編集モーダルを開き直すと選択が消えていた
  const selectedAreaId = (useWatch({ control, name: areaFieldName }) ??
    null) as number | null
  const setSelectedAreaId = (value: number | null): void =>
    setValue(areaFieldName, value, { shouldDirty: true })

  /**
   * 地点選択時、備考が空のときだけ地点マスタの `Site.note` を初期補完する
   * （発地・着地の備考と同じ挙動。入力済みの備考は上書きしない）。
   */
  const copySiteNoteIfEmpty: SingleOnChange<number, SiteSelectListExEntity> = (
    siteId,
    option
  ) => {
    if (siteId == null) return
    if (getValues(noteFieldName)) return
    if (!option?.note) return
    setValue(noteFieldName, option.note, { shouldDirty: true })
  }

  const originSiteId = useWatch({
    control,
    name: `${namePrefix}originSiteId`
  }) as number | null | undefined
  const destSiteId = useWatch({
    control,
    name: `${namePrefix}destSiteId`
  }) as number | null | undefined
  const waypoints = useWatch({ control, name: `${namePrefix}waypoints` }) as
    | WaypointFormValue[]
    | undefined
  const currentSiteId = waypoints?.[index]?.siteId ?? null
  // 発地・着地・他経由地の選択済み地点を無効化（自行の現在値は除く）
  const disabledIds = [
    originSiteId,
    destSiteId,
    ...(waypoints ?? []).map((waypoint) => waypoint?.siteId)
  ].filter((id): id is number => id != null && id !== currentSiteId)

  // 経由地の時間はカレンダー上で矢羽根の時間帯（発時刻の日〜着時刻の日）だけ選べるようにする（#5548）。
  // 時刻は日跨ぎの運行があるため制限しない。手入力の範囲外は保存時の検証で弾く
  const rangeStartMs = toValidDate(entryTimeRange?.startAt)?.getTime() ?? null
  const rangeEndMs = toValidDate(entryTimeRange?.endAt)?.getTime() ?? null
  const entryTimeSelectableRange = {
    from: rangeStartMs != null ? new Date(rangeStartMs) : null,
    to: rangeEndMs != null ? new Date(rangeEndMs) : null
  }

  /**
   * 到着予定時刻の範囲（矢羽根の時間帯）と並び順の検証を、値を確定した時点で行う。
   * 保存まで待たずに該当欄へエラーを出すため。全経由地をまとめて見直し、直った行の
   * エラーは消す。保存時の検証（モーダル側）は後ろ盾として残る。
   *
   * 先に `trigger` でスキーマの検証をやり直してから範囲・並び順のエラーを付ける。
   * react-hook-form の `setError` は `isValid` を false にするが `clearErrors` は戻さないため、
   * `clearErrors` だけだと範囲外の時刻を直してもエラー表示が消えるだけで保存ボタンが
   * 押せないまま残る。`trigger` は resolver の結果で `isValid` を取り直す。
   * 発着の時間制限の欄（TimeRestrictionRows）と同じやり方。
   *
   * `trigger` は非同期なので、入力中は呼び出しが重なる（文字を打つ途中の値で日時ピッカーが
   * onChange を撃つ）。古い呼び出しが後から setError すると直した後にエラーが残るため、
   * 全行で共有する連番（`entryTimeValidationSeqRef`）で最新の呼び出しだけがエラーを付ける
   */
  const validateEntryTimes = useCallback(
    async (changedIndex: number | null, nextValue?: Date | string | null) => {
      const seq = ++entryTimeValidationSeqRef.current
      const current =
        (getValues(`${namePrefix}waypoints`) as
          | WaypointFormValue[]
          | undefined) ?? []
      const next = current.map((waypoint, i) =>
        i === changedIndex ? { ...waypoint, entryTime: nextValue } : waypoint
      )
      const issues = validateWaypointEntryTimes(next, {
        startAt: rangeStartMs != null ? new Date(rangeStartMs) : null,
        endAt: rangeEndMs != null ? new Date(rangeEndMs) : null
      })
      const entryTimeFieldNames = next.map(
        (_, i) => `${namePrefix}waypoints.${i}.entryTime`
      )
      entryTimeFieldNames.forEach((name) => clearErrors(name))
      await trigger(entryTimeFieldNames)
      if (seq !== entryTimeValidationSeqRef.current) return
      issues.forEach(({ index: i, message }) =>
        setError(`${namePrefix}waypoints.${i}.entryTime`, {
          type: 'manual',
          message
        })
      )
    },
    [
      entryTimeValidationSeqRef,
      getValues,
      namePrefix,
      rangeStartMs,
      rangeEndMs,
      clearErrors,
      trigger,
      setError
    ]
  )

  // 矢羽根の時間帯が変わったら（発着の時間欄を直したとき・開いた直後）、今の値で見直す。
  // 全行を見るので先頭の行だけが走らせる
  useEffect(() => {
    if (!showEntryTime || index !== 0) return
    void validateEntryTimes(null)
  }, [showEntryTime, index, validateEntryTimes])

  return (
    // 経由地 1 件を枠で囲い、番号見出しと削除ボタンを 1 行目に置く。
    // 複数の経由地が並んだときに境界が見えるようにするため。
    // 中の入力行は「幅固定ラベル + 入力欄」で揃える（地点だけ全幅で伸びないように）
    <div className="mb-2 rounded border border-gray-300 p-2">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-bold">
          {LABELS.WAYPOINT}
          {index + 1}
        </span>
        <Button
          type="text"
          size="small"
          icon={<FaTimes />}
          onClick={() => onRemove(index)}
          aria-label={`${LABELS.WAYPOINT}${index + 1}`}
        />
      </div>
      <div className="mb-2 flex items-center gap-2">
        <span className="w-20 shrink-0">{LABELS.AREA}</span>
        <AreaSelect
          showSearch
          areas={areas}
          mode={undefined}
          value={selectedAreaId}
          placeholder={STATUSES.UNSELECTED}
          popupMatchSelectWidth={false}
          allowClear
          className="flex-1"
          onChange={(value) => {
            setSelectedAreaId(value)
            // エリア変更に伴い当該行の地点を未選択へクリア
            resetField(fieldName, { defaultValue: null })
          }}
        />
      </div>
      <Controller
        control={control}
        name={fieldName}
        render={({ field, fieldState: { error } }) => (
          <div className="flex items-center gap-2">
            <label htmlFor={field.name} className="w-20 shrink-0">
              {LABELS.STOP_SITE}
            </label>
            <Form.Item
              validateStatus={error?.message ? 'error' : undefined}
              htmlFor={field.name}
              help={error?.message}
              className="m-0 min-w-0 flex-1"
            >
              <SiteSelectWithLocation
                {...field}
                id={field.name}
                tenantKey={tenantKey}
                showSearch
                placeholder={STATUSES.UNSELECTED}
                disabledIds={disabledIds}
                value={field.value ?? undefined}
                allowClear
                onChange={(value, option) => {
                  field.onChange(value ?? null)
                  copySiteNoteIfEmpty(value, option)
                }}
                selectedAreaId={selectedAreaId}
                customerId={customerId}
                enableCustomerLink
              />
            </Form.Item>
          </div>
        )}
      />
      {/* 経由地での作業内容（#4813）と備考。どちらも地点とセットで見る項目なので地点
            プルダウンの直下に 1 行で並べる（作業内容は 3 択なので幅を固定し、備考に残り幅を渡す）。
            備考は発着地備考と同じく、配車板ホバーの配送ルート表示にその地点の行として出る */}
      <div className="mt-2 flex items-center gap-2">
        <label htmlFor={workTypeFieldName} className="w-20 shrink-0">
          {LABELS.WORK_TYPE}
        </label>
        <Controller
          control={control}
          name={workTypeFieldName}
          render={({ field, fieldState: { error } }) => (
            <Form.Item
              htmlFor={field.name}
              validateStatus={error?.message ? 'error' : undefined}
              help={error?.message}
              className="m-0 w-36 shrink-0"
            >
              <Select
                id={field.name}
                data-testid="shipment-order-waypoint-row__work-type"
                options={WORK_TYPE_OPTIONS}
                value={field.value ?? undefined}
                onChange={(value) => field.onChange(value)}
                onBlur={field.onBlur}
                className="w-full"
              />
            </Form.Item>
          )}
        />
        <label htmlFor={noteFieldName} className="ml-2 shrink-0">
          {LABELS.NOTE}
        </label>
        <Controller
          control={control}
          name={noteFieldName}
          render={({ field, fieldState: { error } }) => (
            <Form.Item
              htmlFor={field.name}
              validateStatus={error?.message ? 'error' : undefined}
              help={error?.message}
              className="m-0 flex-1"
            >
              <Input
                id={field.name}
                {...field}
                value={field.value ?? ''}
                allowClear
              />
            </Form.Item>
          )}
        />
      </div>
      {/* 到着予定時刻（#5548）。発着地の時間欄と同じ日時ピッカーで、未入力を許す。
            範囲（発時刻〜着時刻）と並び順の検証は値を確定した時点で行い、この欄にエラーを出す */}
      {showEntryTime && (
        <div className="mt-2 flex items-center gap-2">
          <label htmlFor={entryTimeFieldName} className="w-20 shrink-0">
            {LABELS.TIME}
          </label>
          <Controller
            control={control}
            name={entryTimeFieldName}
            render={({ field, fieldState: { error } }) => (
              <Form.Item
                htmlFor={field.name}
                validateStatus={error?.message ? 'error' : undefined}
                help={error?.message}
                className="m-0"
              >
                <NormalizedSingleFieldDateTimePicker
                  id={field.name}
                  data-testid="shipment-order-waypoint-row__entry-time"
                  allowClear
                  style={{ width: '180px' }}
                  selectableDateRange={entryTimeSelectableRange}
                  // 未入力で開いたときのカレンダーは矢羽根の発日で開く。今日で開くと日付を
                  // 選ばずに時刻だけ選んだとき今日の日付が入り、矢羽根の時間帯の外になる
                  defaultPickerValue={
                    entryTimeSelectableRange.from ?? undefined
                  }
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) => {
                    // 値はスキーマ検証を走らせずに入れる。走らせると直後に付ける範囲エラーが
                    // スキーマ（形式のみ）の結果で消される
                    setValue(entryTimeFieldName, date, {
                      shouldDirty: true,
                      shouldValidate: false
                    })
                    void validateEntryTimes(index, date)
                  }}
                  onInvalidText={(message) =>
                    setError(entryTimeFieldName, {
                      type: 'manual',
                      message
                    })
                  }
                  onValidText={() =>
                    void validateEntryTimes(
                      index,
                      getValues(entryTimeFieldName) as Date | string | null
                    )
                  }
                />
              </Form.Item>
            )}
          />
        </div>
      )}
    </div>
  )
}

/** フォーム値（Date か ISO 文字列）を Date に。無効なら null */
const toValidDate = (value: Date | string | null | undefined): Date | null => {
  if (value == null || value === '') return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}
